import { useEffect, useRef, useState } from 'react';
import { getDriverLiveSnapshot, streamDriverLive } from './driverLiveApi';
import { deriveDriverLiveDisplayStatus } from './driverLiveUi';

const STREAM_WATCHDOG_MS = 25000;
const FALLBACK_AFTER_MS = 7000;
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

export default function useDriverLive(raceId, driverNumber, accessToken, onAccessExpired, mode = 'live', expectedPubVersion = null) {
  const [snapshot, setSnapshot] = useState(null);
  const [transportStatus, setTransportStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [clock, setClock] = useState(() => Date.now());
  const expiredRef = useRef(onAccessExpired);
  expiredRef.current = onAccessExpired;

  useEffect(() => {
    const timer = setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setSnapshot(null);
    setError(null);
    if (!raceId || !driverNumber || !accessToken || mode === 'pending') {
      setTransportStatus('idle');
      return undefined;
    }

    let disposed = false;
    let lastStreamActivity = 0;
    let polling = false;
    let reconnectDelay = 1500;
    let accessHasExpired = false;
    let activeStreamController = null;
    let lastSseAt = 0;
    let lastAcceptedAt = 0;
    let latestPlayback = null;
    let streamSequence = 0;
    const controllers = new Set();

    const accept = (data, source = 'sse', startedAt = Date.now()) => {
      if (disposed || mode === 'pending') return;
      if (mode === 'pub') {
        if (data?.playback?.mode !== 'pub' ||
            (expectedPubVersion != null && data.playback.settingsVersion !== expectedPubVersion)) return;
        if (source === 'http' && startedAt < lastSseAt) return;
        const targetMs = Date.parse(data.playback.targetTime || '');
        if (!Number.isFinite(targetMs)) return;
        const epoch = String(data.playback.epoch);
        if (latestPlayback) {
          if (latestPlayback.epoch === epoch && targetMs < latestPlayback.targetMs) return;
          if (latestPlayback.epoch !== epoch && source === 'http' && Date.now() - lastSseAt < 5000) return;
        }
        latestPlayback = { epoch, targetMs };
      }
      lastAcceptedAt = Date.now();
      setSnapshot(data);
      setError(null);
      setTransportStatus(data?.playback?.mode === 'pub'
        ? (data.playback.status === 'ready' ? 'connected' : 'recovering')
        : data?.feed?.connection === 'connected' ? 'connected' : 'recovering');
    };

    const accessExpired = code => {
      if (disposed) return;
      accessHasExpired = true;
      setTransportStatus('expired');
      expiredRef.current?.(code);
    };

    const onStreamActivity = () => { lastStreamActivity = Date.now(); };
    const onStreamData = data => {
      lastStreamActivity = Date.now();
      reconnectDelay = 1500;
      lastSseAt = Date.now();
      streamSequence += 1;
      accept(data, 'sse', lastSseAt);
    };

    const connectStreamOnce = async controller => {
      await streamDriverLive(raceId, driverNumber, {
            signal: controller.signal,
            onActivity: onStreamActivity,
            onData: onStreamData,
            onAccessExpired: accessExpired,
            onStatus: status => { if (!disposed && mode === 'pub') {
              streamSequence += 1;
              setSnapshot(null); setError(status?.reason || status?.status || 'TV sync unavailable');
              setTransportStatus('recovering');
            } },
            mode,
          });
    };

    const streamLoop = async () => {
      while (!disposed && !accessHasExpired) {
        const controller = new AbortController();
        activeStreamController = controller;
        controllers.add(controller);
        lastStreamActivity = Date.now();
        try {
          setTransportStatus('connecting');
          await connectStreamOnce(controller);
          if (!disposed && !accessHasExpired) setTransportStatus('recovering');
        } catch (err) {
          if (disposed) break;
          if (err.name === 'AbortError') {
            if (!accessHasExpired) setTransportStatus('recovering');
          } else if ([401, 403, 410].includes(err.status)) {
            accessExpired(err.code);
            break;
          } else {
            if (mode === 'pub') setSnapshot(null);
            setError(err.message || 'Live stream interrupted');
            setTransportStatus('recovering');
          }
        } finally {
          controllers.delete(controller);
          if (activeStreamController === controller) activeStreamController = null;
        }
        if (!disposed && !accessHasExpired) {
          await sleep(reconnectDelay);
          reconnectDelay = Math.min(reconnectDelay * 1.7, 10000);
        }
      }
    };

    const fallback = async () => {
      if (disposed || accessHasExpired || polling) return;
      if (lastStreamActivity && Date.now() - lastStreamActivity < FALLBACK_AFTER_MS) return;
      polling = true;
      const controller = new AbortController();
      const startedAt = Date.now();
      const sequenceAtStart = streamSequence;
      controllers.add(controller);
      try {
        const data = await getDriverLiveSnapshot(raceId, driverNumber, controller.signal, mode);
        if (!disposed && sequenceAtStart === streamSequence && (!lastStreamActivity || Date.now() - lastStreamActivity >= FALLBACK_AFTER_MS)) accept(data, 'http', startedAt);
      } catch (err) {
        if (!disposed && !controller.signal.aborted && sequenceAtStart === streamSequence && startedAt >= lastSseAt && startedAt >= lastAcceptedAt) {
          if ([401, 403, 410].includes(err.status)) accessExpired(err.code);
          else {
            if (mode === 'pub') setSnapshot(null);
            setError(err.message || 'Live data temporarily unavailable');
            setTransportStatus('recovering');
          }
        }
      } finally {
        controllers.delete(controller);
        polling = false;
      }
    };

    const watchdog = () => {
      if (disposed || accessHasExpired || !activeStreamController || !lastStreamActivity) return;
      if (Date.now() - lastStreamActivity > STREAM_WATCHDOG_MS) {
        // A fetch stream can stay pending forever if the connection silently dies.
        // Abort it deliberately; streamLoop reconnects and shared REST recovery fills the gap.
        activeStreamController.abort();
        setTransportStatus('recovering');
      }
    };

    streamLoop();
    fallback();
    const fallbackTimer = setInterval(fallback, 2500);
    const watchdogTimer = setInterval(watchdog, 2000);
    return () => {
      disposed = true;
      clearInterval(fallbackTimer);
      clearInterval(watchdogTimer);
      for (const controller of controllers) controller.abort();
    };
  }, [raceId, driverNumber, accessToken, mode, expectedPubVersion]);

  // Historical data freshness is measured at its playhead, not wall time.
  // Render-time protection, not an after-paint effect: mode and settings
  // changes cannot reveal the previous live/pub sample for even one frame.
  const activeSnapshot = snapshot && (mode === 'pub'
    ? snapshot.playback?.mode === 'pub' &&
      (expectedPubVersion == null || snapshot.playback.settingsVersion === expectedPubVersion)
    : snapshot.playback?.mode !== 'pub') ? snapshot : null;
  const historicalNow = Date.parse(activeSnapshot?.playback?.targetTime || '');
  const displayNow = mode === 'pub' && Number.isFinite(historicalNow) ? historicalNow : clock;
  const displayStatus = deriveDriverLiveDisplayStatus(activeSnapshot, transportStatus, displayNow);
  return { snapshot: activeSnapshot, transportStatus, displayStatus, error, now: displayNow };
}
